import { 
  collection, 
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { validateEmail } from '@/utils/emailUtils';
import { generateUniqueHexCode } from '@/utils/codeGenerator';
import { toast } from 'react-hot-toast';
import { Evaluator, EvaluatorInput } from '@/types/evaluator';

export async function getEvaluatorsByUserId(userId: string): Promise<Evaluator[]> {
  if (!userId) throw new Error('User ID is required');

  try {
    const evaluatorsRef = collection(db, collections.evaluators);
    const q = query(evaluatorsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id.split('_')[1], // Extract original evaluator ID
      ...doc.data()
    })) as Evaluator[];
  } catch (error: any) {
    console.error('Error fetching evaluators:', error);
    throw new Error('Failed to load evaluators');
  }
}

// Helper function to generate evaluator document ID
function generateEvaluatorDocId(evaluatorId: string, userId: string): string {
  return `evaluator_${evaluatorId}_${userId}`;
}

export async function validateEvaluatorEmail(userId: string, email: string): Promise<{
  isValid: boolean;
  message?: string;
}> {
  try {
    if (!validateEmail(email)) {
      return {
        isValid: false,
        message: 'Invalid email format'
      };
    }

    const evaluatorsRef = collection(db, collections.evaluators);
    const q = query(evaluatorsRef, 
      where('userId', '==', userId),
      where('email', '==', email.toLowerCase().trim())
    );
    const snapshot = await getDocs(q);
    
    return {
      isValid: snapshot.empty,
      message: snapshot.empty ? undefined : 'An evaluator with this email already exists'
    };
  } catch (error) {
    console.error('Error validating evaluator email:', error);
    return {
      isValid: false,
      message: 'Failed to validate email'
    };
  }
}

export async function getEvaluatorById(evaluatorId: string): Promise<Evaluator | null> {
  if (!evaluatorId) throw new Error('Evaluator ID is required');

  try {
    const docId = evaluatorId.includes('_') ? evaluatorId : `evaluator_${evaluatorId}`;
    const evaluatorRef = doc(db, collections.evaluators, docId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      return null;
    }

    const evaluatorData = evaluatorDoc.data();
    const userRef = doc(db, collections.users, evaluatorData.userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('Subject not found');
    }

    const userData = userDoc.data();

    return {
      id: evaluatorDoc.id,
      ...evaluatorData,
      subjectName: `${userData.firstName} ${userData.lastName}`
    } as Evaluator;
  } catch (error) {
    console.error('Error getting evaluator:', error);
    throw new Error('Failed to retrieve evaluator');
  }
}

export async function createEvaluator(userId: string, data: EvaluatorInput): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    // Validate email format
    if (!validateEmail(data.email)) {
      throw new Error('Invalid email format');
    }

    const batch = writeBatch(db);
    const evaluatorsRef = collection(db, collections.evaluators);
    
    // Check for existing evaluator with same email
    const q = query(evaluatorsRef, 
      where('userId', '==', userId),
      where('email', '==', data.email.toLowerCase().trim())
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      throw new Error('An evaluator with this email already exists');
    }

    // Generate a unique evaluator ID
    const evaluatorId = doc(collection(db, 'temp')).id;
    const docId = generateEvaluatorDocId(evaluatorId, userId);
    const newEvaluatorRef = doc(evaluatorsRef, docId);

    // Generate unique hex code for verification
    const uniqueCode = generateUniqueHexCode();

    // Create new evaluator document
    batch.set(newEvaluatorRef, {
      id: evaluatorId,
      userId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.toLowerCase().trim(),
      uniqueCode,
      contextualized: false,
      evaluationCompleted: false,
      emailVerified: false,
      lastReminderSentAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update user's evaluator count
    const userRef = doc(db, collections.users, userId);
    const userData = await getDoc(userRef);
    
    if (userData.exists()) {
      const currentCount = userData.data().evaluatorsCount || 0;
      batch.update(userRef, {
        evaluatorsCount: currentCount + 1,
        evaluatorsNominated: true,
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
    toast.success('Evaluator added successfully');
  } catch (error: any) {
    console.error('Error creating evaluator:', error);
    throw new Error(error.message || 'Failed to create evaluator');
  }
}

export async function deleteEvaluator(evaluatorId: string, userId: string): Promise<void> {
  if (!evaluatorId || !userId) {
    throw new Error('Evaluator ID and User ID are required');
  }

  try {
    const batch = writeBatch(db);
    const docId = generateEvaluatorDocId(evaluatorId, userId);
    const evaluatorRef = doc(db, collections.evaluators, docId);
    
    // Verify evaluator exists and belongs to user
    const evaluatorDoc = await getDoc(evaluatorRef);
    if (!evaluatorDoc.exists()) {
      throw new Error('Evaluator not found');
    }

    const evaluatorData = evaluatorDoc.data();
    if (evaluatorData.userId !== userId) {
      throw new Error('Unauthorized to delete this evaluator');
    }

    if (evaluatorData.evaluationCompleted) {
      throw new Error('Cannot delete an evaluator who has completed their evaluation');
    }

    // Delete evaluator document
    batch.delete(evaluatorRef);

    // Update user's evaluator count
    const userRef = doc(db, collections.users, userId);
    const userData = await getDoc(userRef);
    
    if (userData.exists()) {
      const currentCount = userData.data().evaluatorsCount || 0;
      batch.update(userRef, {
        evaluatorsCount: Math.max(0, currentCount - 1),
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error: any) {
    console.error('Error deleting evaluator:', error);
    throw new Error(error.message || 'Failed to delete evaluator');
  }
}

export async function updateEvaluator(
  evaluatorId: string, 
  userId: string, 
  data: EvaluatorInput
): Promise<void> {
  if (!evaluatorId || !userId) {
    throw new Error('Evaluator ID and User ID are required');
  }

  try {
    const docId = generateEvaluatorDocId(evaluatorId, userId);
    const evaluatorRef = doc(db, collections.evaluators, docId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      throw new Error('Evaluator not found');
    }

    const evaluatorData = evaluatorDoc.data();
    if (evaluatorData.userId !== userId) {
      throw new Error('Unauthorized to update this evaluator');
    }

    if (evaluatorData.evaluationCompleted) {
      throw new Error('Cannot update an evaluator who has completed their evaluation');
    }

    await updateDoc(evaluatorRef, {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      updatedAt: serverTimestamp()
    });
  } catch (error: any) {
    console.error('Error updating evaluator:', error);
    throw new Error(error.message || 'Failed to update evaluator');
  }
}